import { LanguageType, SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';

import { Organization } from '../../../domain/backoffice/organization/Organization';
import { Project } from '../../../domain/backoffice/project/Project';
import { User } from '../../../domain/backoffice/user/User';
import { Translation } from '../../../domain/translation/Translation';
import { BackofficeActions, BackofficeConstants } from './actions';
import {
  OrganizationsTableColumn,
  getOrganizationsTableColumns,
  setOrganizationsTableColumns,
} from './tableColumns/organizations';
import { ProjectsTableColumn, getProjectsTableColumns, setProjectsTableColumns } from './tableColumns/projects';
import {
  TranslationsTableColumn,
  getTranslationsTableColumns,
  setTranslationsTableColumns,
} from './tableColumns/translations';
import { UsersTableColumn, getUsersTableColumns, setUsersTableColumns } from './tableColumns/users';

export type TranslationsData = { [identifier: string]: { [locale: string]: string | undefined } };

export type DomainData = { locales: LanguageType[]; translations: TranslationsData } | undefined;

export interface BackofficeState {
  users: {
    data: PaginatedResult<User>;
    predicates: Array<SearchPredicate>;
    predicatesBase64: string;
    tableColumns: Array<UsersTableColumn>;
  };
  organizations: {
    data: PaginatedResult<Organization>;
    predicates: Array<SearchPredicate>;
    predicatesBase64: string;
    tableColumns: Array<OrganizationsTableColumn>;
  };
  projects: {
    data: PaginatedResult<Project>;
    predicates: Array<SearchPredicate>;
    predicatesBase64: string;
    tableColumns: Array<ProjectsTableColumn>;
  };
  translations: {
    data: PaginatedResult<Translation>;
    predicates: Array<SearchPredicate>;
    predicatesBase64: string;
    tableColumns: Array<TranslationsTableColumn>;
  };
}

export const getInitialState = (): BackofficeState => ({
  users: {
    data: {
      limit: 20,
      offset: 0,
      total: 0,
      docs: [],
    },
    predicates: [],
    predicatesBase64: ``,
    tableColumns: getUsersTableColumns(),
  },
  organizations: {
    data: {
      limit: 20,
      offset: 0,
      total: 0,
      docs: [],
    },
    predicates: [],
    predicatesBase64: ``,
    tableColumns: getOrganizationsTableColumns(),
  },
  projects: {
    data: {
      limit: 20,
      offset: 0,
      total: 0,
      docs: [],
    },
    predicates: [],
    predicatesBase64: ``,
    tableColumns: getProjectsTableColumns(),
  },
  translations: {
    data: {
      limit: 20,
      offset: 0,
      total: 0,
      docs: [],
    },
    predicates: [],
    predicatesBase64: ``,
    tableColumns: getTranslationsTableColumns(),
  },
});

export const BackofficeReducer = (state: BackofficeState, action: BackofficeActions): BackofficeState => {
  switch (action.type) {
    case BackofficeConstants.SET_ORGANIZATIONS_TABLE_COLUMNS:
      setOrganizationsTableColumns(action.payload);
      return {
        ...state,
        organizations: {
          ...state.organizations,
          tableColumns: action.payload,
        },
      };
    case BackofficeConstants.SEARCH_ORGANIZATIONS_SUCCESS:
      return {
        ...state,
        organizations: {
          ...state.organizations,
          data: action.payload.organizations,
        },
      };
    case BackofficeConstants.SET_SEARCH_ORGANIZATIONS_PREDICATES: {
      const { predicates, predicatesBase64 } = action.payload;
      return {
        ...state,
        organizations: {
          ...state.organizations,
          predicates,
          predicatesBase64,
        },
      };
    }
    case BackofficeConstants.SET_PROJECTS_TABLE_COLUMNS:
      setProjectsTableColumns(action.payload);
      return {
        ...state,
        projects: {
          ...state.projects,
          tableColumns: action.payload,
        },
      };
    case BackofficeConstants.SEARCH_PROJECTS_SUCCESS:
      return {
        ...state,
        projects: {
          ...state.projects,
          data: action.payload.projects,
        },
      };
    case BackofficeConstants.SET_SEARCH_PROJECTS_PREDICATES: {
      const { predicates, predicatesBase64 } = action.payload;
      return {
        ...state,
        projects: {
          ...state.projects,
          predicates,
          predicatesBase64,
        },
      };
    }
    case BackofficeConstants.SET_USERS_TABLE_COLUMNS:
      setUsersTableColumns(action.payload);
      return {
        ...state,
        users: {
          ...state.users,
          tableColumns: action.payload,
        },
      };
    case BackofficeConstants.SEARCH_USERS_SUCCESS:
      return {
        ...state,
        users: {
          ...state.users,
          data: action.payload.users,
        },
      };
    case BackofficeConstants.SET_SEARCH_USERS_PREDICATES: {
      const { predicates, predicatesBase64 } = action.payload;
      return {
        ...state,
        users: {
          ...state.users,
          predicates,
          predicatesBase64,
        },
      };
    }
    case BackofficeConstants.SET_TRANSLATIONS_TABLE_COLUMNS:
      setTranslationsTableColumns(action.payload);
      return {
        ...state,
        translations: {
          ...state.translations,
          tableColumns: action.payload,
        },
      };
    case BackofficeConstants.SEARCH_TRANSLATIONS_SUCCESS:
      return {
        ...state,
        translations: {
          ...state.translations,
          data: action.payload.translations,
        },
      };
    case BackofficeConstants.SET_SEARCH_TRANSLATIONS_PREDICATES: {
      const { predicates, predicatesBase64 } = action.payload;
      return {
        ...state,
        translations: {
          ...state.translations,
          predicates,
          predicatesBase64,
        },
      };
    }
    default:
      return state;
  }
};
